/**
 * THE ONLY SOURCE OF CONTEXT. 09 §3.4.
 *
 *   "Company and period are selected in the context bar and nowhere else."
 *
 * That sentence is the most-cited UI rule in the accepted architecture, and Gate A explains why:
 * 70 distinct authentication expressions, 12 company-guard expressions and company resolution
 * differing per module (`?db` in FS/bank/grants, `_Cc2Slug` in cc2, `Test-FinsiteVisibleToUser` in
 * finsite). A frontend grown over that surface grows a company dropdown per module. This file exists
 * so that cannot happen again.
 *
 * The API is split in two on purpose:
 *
 *   useWorkContext()      — READ. Any screen may ask what it is looking at (it needs the companyUid
 *                           to fetch). Read-only: there is no setter on the returned object.
 *   useContextSelection() — CHOOSE. Only <ContextBar> may call it, and the parameter type admits
 *                           exactly one caller so a second call site fails to compile.
 *
 * Nothing here decides permission. `visibleCompanies` and `modules` are the server's answers,
 * rendered as given (09 §5.1 / CONFLICT C-1).
 */

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type {
  CompanySummary,
  ModuleEntitlement,
  PeriodSummary,
  SessionContext,
  SessionSummary,
  TenantSummary,
} from '../api/contract';
import type { ServerData } from '../api/useServerData';

/** 09 §3.1 — the six slots of the bar, in RTL reading order: המשרד ‹ החברה ‹ המודול ‹ הישות ‹ התקופה. */
export interface WorkContext {
  readonly session: SessionSummary | null;
  /** PLATFORM and TENANT never bleed and are never simultaneous (04 I6). */
  /**
   * `null` until `auth.whoami.read` answers. CM-014: there is no default, because a default is a
   * surface somebody was granted without a session saying so — and TENANT was the old one.
   */
  readonly surface: SessionContext | null;
  readonly tenant: TenantSummary | null;
  readonly company: CompanySummary | null;
  readonly module: ModuleEntitlement | null;
  /** 09 §3.1 "הישות" — set by a child screen bound to exactly one row. */
  readonly entityLabelHe: string | null;
  readonly period: PeriodSummary | null;

  /** The server's answers, rendered as given. Never filtered client-side. */
  readonly visibleCompanies: readonly CompanySummary[];
  /** The read `visibleCompanies` was derived from. See `ContextProviderProps.companiesResult`. */
  readonly companiesResult: ServerData<{ readonly companies: readonly CompanySummary[] }>;
  readonly modules: readonly ModuleEntitlement[];
  readonly periods: readonly PeriodSummary[];
}

/**
 * The selection API. Note what is NOT here: nothing that grants, nothing that filters a company
 * list by a client-side rule. Selecting rewrites the address; the gate re-decides on the next call.
 */
export interface ContextSelection {
  selectTenant(tenantUid: string): void;
  selectCompany(companyUid: string): void;
  selectPeriod(periodUid: string | null): void;
}

/*
 * WHAT IS NOT IN `ContextSelection`, AND WHY — **CM-014.**
 *
 * There was a `switchSurface(surface)` here, and a button in the context bar that called it. It set
 * a piece of React state, so a person on the tenant surface could put themselves on the platform
 * surface by clicking, and the shell believed them. The address did the same thing independently:
 * `/platform` minted a PLATFORM session, so typing a URL granted a surface.
 *
 * OA-0001 §2.1 withdrew the two-context identity outright — an account's context comes from
 * `app_user.account_kind` at authentication and CANNOT change. So the surface is not state here,
 * it is a READ of `session.sessionContext`, which comes from `auth.whoami.read` and therefore from
 * the server. There is no setter to call, no flag to set and no address that grants anything.
 *
 * A platform operator who needs to be inside a customer's data does it the one way the model has: a
 * support grant, requested, approved by a second principal, consented to where the customer requires
 * it, time-boxed and audited on every call. That is a different mechanism, not a faster version of
 * this one.
 */

/**
 * The single admissible caller. Widening this union re-opens the defect the rule exists to prevent,
 * so widening it is the change a reviewer must refuse.
 */
export type ContextSelectorCaller = 'ContextBar';

interface ContextStore extends WorkContext {
  readonly selection: ContextSelection;
  setEntityLabel(label: string | null): void;
  setModuleByCode(moduleCode: string | null): void;
  setCompanyByUid(companyUid: string | null): void;
}

const StoreContext = createContext<ContextStore | null>(null);

function useStore(): ContextStore {
  const store = useContext(StoreContext);
  if (store === null) {
    throw new Error('Rendered outside <ContextProvider>. Context has exactly one source.');
  }
  return store;
}

/** READ. Available to every screen; carries no setter. */
export function useWorkContext(): WorkContext {
  return useStore();
}

/**
 * CHOOSE. 09 §3.4 — the context bar and nowhere else.
 *
 * The `caller` argument is not ceremony: `ContextSelectorCaller` is a single-member union, so a
 * module that tries to grow its own company dropdown fails at compile time, and the dev-time throw
 * below catches a cast. `src/shell/contextSingleSource.test.ts` additionally scans the source tree.
 */
export function useContextSelection(caller: ContextSelectorCaller): ContextSelection {
  const store = useStore();
  if (import.meta.env.DEV && caller !== 'ContextBar') {
    throw new Error(
      '09 §3.4: company and period are selected in the context bar and nowhere else. ' +
        'No module ever gets its own company dropdown again.',
    );
  }
  return store.selection;
}

/**
 * Set by a screen to fill the המודול / הישות slots of the bar. This is the bar READING the current
 * screen, which is the opposite direction from choosing — the screen never chooses a company here.
 */
export function useDeclareScreenContext(): {
  setEntityLabel: (label: string | null) => void;
  setModuleByCode: (moduleCode: string | null) => void;
  setCompanyByUid: (companyUid: string | null) => void;
} {
  const store = useStore();
  return {
    setEntityLabel: store.setEntityLabel,
    setModuleByCode: store.setModuleByCode,
    setCompanyByUid: store.setCompanyByUid,
  };
}

export interface ContextProviderProps {
  readonly session: SessionSummary | null;
  readonly companies: readonly CompanySummary[];
  /**
   * The READ that produced `companies`, when there was one. **Phase 2 §6, vertical A.**
   *
   * `visibleCompanies` is a list, and a list cannot say whether it is empty because the office has
   * no clients or because the read was refused. A screen that must tell those apart — the picker,
   * whose entire subject is "which companies may I open" — renders this through `<ServerRead>`;
   * every other consumer keeps using `visibleCompanies` and is unaffected.
   *
   * There is still ONE SOURCE: the list is DERIVED from this result by whoever passes both, and a
   * provider that let them disagree would be the two-answers problem 04 I6 exists to prevent. It is
   * optional so that the many tests which supply a plain array do not have to state a read that
   * never happened.
   */
  readonly companiesResult?: ServerData<{ readonly companies: readonly CompanySummary[] }>;
  readonly modules: readonly ModuleEntitlement[];
  readonly periods: readonly PeriodSummary[];
  /** Injected by <App> so this file has no router dependency and stays unit-testable. */
  readonly navigate: (path: string) => void;
  readonly children: ReactNode;
}

export function ContextProvider(props: ContextProviderProps): JSX.Element {
  const { session, companies, companiesResult, modules, periods, navigate, children } = props;

  // NOT STATE. CM-014: the surface is whatever the server said the session is, and until the
  // server has answered there is no surface — `null` renders the signed-out shell rather than
  // guessing TENANT, because a guessed surface is a surface somebody was granted by a default.
  const surface: SessionContext | null = session?.sessionContext ?? null;
  const [tenantUid, setTenantUid] = useState<string | null>(session?.activeTenantUid ?? null);
  const [companyUid, setCompanyUid] = useState<string | null>(null);
  const [moduleCode, setModuleCode] = useState<string | null>(null);
  const [periodUid, setPeriodUid] = useState<string | null>(null);
  const [entityLabelHe, setEntityLabelHe] = useState<string | null>(null);

  const tenant = useMemo(
    () => session?.tenants.find((t) => t.tenantUid === tenantUid) ?? null,
    [session, tenantUid],
  );
  const company = useMemo(
    () => companies.find((c) => c.companyUid === companyUid) ?? null,
    [companies, companyUid],
  );
  const moduleEntitlement = useMemo(
    () => modules.find((m) => m.moduleCode === moduleCode) ?? null,
    [modules, moduleCode],
  );
  const period = useMemo(
    () => periods.find((p) => p.periodUid === periodUid) ?? null,
    [periods, periodUid],
  );

  const selectCompany = useCallback(
    (nextCompanyUid: string) => {
      setCompanyUid(nextCompanyUid);
      // 09 §5.1 "On switch": land on the same module for the new company where that applies,
      // otherwise on the new company's home — never a blank screen and never an error.
      const stillApplies =
        moduleCode !== null && modules.some((m) => m.moduleCode === moduleCode && m.available);
      const target = stillApplies
        ? `/c/${nextCompanyUid}/m/${moduleCode ?? ''}`
        : `/c/${nextCompanyUid}`;
      navigate(periodUid === null ? target : `${target}?period=${periodUid}`);
    },
    [moduleCode, modules, navigate, periodUid],
  );

  const selection = useMemo<ContextSelection>(
    () => ({
      selectTenant: (nextTenantUid: string) => {
        setTenantUid(nextTenantUid);
        // A tenant switch invalidates every narrower slot. Nothing carries over.
        setCompanyUid(null);
        setModuleCode(null);
        setPeriodUid(null);
        setEntityLabelHe(null);
        navigate('/companies');
      },
      selectCompany,
      selectPeriod: (nextPeriodUid: string | null) => setPeriodUid(nextPeriodUid),
    }),
    [navigate, selectCompany],
  );

  const store = useMemo<ContextStore>(
    () => ({
      session,
      surface,
      tenant,
      company,
      module: moduleEntitlement,
      entityLabelHe,
      period,
      visibleCompanies: companies,
      // Absent means "nobody read anything" — which is what a test supplying a fixed array is
      // saying, and it is `loaded` rather than `loading` so no screen sits on a skeleton forever.
      companiesResult: companiesResult ?? { state: 'loaded', data: { companies } },
      modules,
      periods,
      selection,
      setEntityLabel: setEntityLabelHe,
      setModuleByCode: setModuleCode,
      setCompanyByUid: setCompanyUid,
    }),
    [
      session,
      surface,
      tenant,
      company,
      moduleEntitlement,
      entityLabelHe,
      period,
      companies,
      companiesResult,
      modules,
      periods,
      selection,
    ],
  );

  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
}
